import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { UppyFile } from '@uppy/core';
import ObjectID from 'bson-objectid';
import config from 'config';
import {
  $createParagraphNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_NORMAL,
  FOCUS_COMMAND,
} from 'lexical';

import { Attach2Fill } from '@lyearn/icons';
import { useUppy } from '@lyearn/shared/hooks/useUppy';
import { cn } from '@lyearn/style';
import { DeprecatedIconButton, Stack } from '@lyearn/ui';
import FileInput from '@/components/FileInput';
import { useUppyLazyGetInstance } from '@/components/UppyProvider';
import currentUser from '@/helper/currentUser';
import { request } from '@/helper/request';
import { useAppConfig } from '@/hooks/useAppConfig';

import { $createAudioNode } from '../../nodes/AudioNode';
import { $createFileNode } from '../../nodes/FileNode';
import { $createImageNode } from '../../nodes/ImageNode';
import {
  $createUploadPlaceholderNode,
  UploadPlaceholderNode,
} from '../../nodes/UploadPlaceholderNode';
import { $createVideoNode } from '../../nodes/VideoNode';

interface CustomMetadata {
  lexicalNodeKey?: string;
}

type CustomUppyFile = UppyFile & {
  meta: UppyFile['meta'] & CustomMetadata;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export default function UppyPlugin({ className }: { className?: string }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { site: siteConfig } = useAppConfig();
  const { getUppyInstance, isReady } = useUppyLazyGetInstance();
  const [editor] = useLexicalComposerContext();
  const [isEditorActive, setIsEditorActive] = useState(false);
  const id = new ObjectID().toHexString();

  const referenceId = useMemo(() => `media-plugin-${id}`, [id]);

  //hide uppy plugin when editor is not active
  useEffect(() => {
    setIsEditorActive(editor.getRootElement()?.contains(document.activeElement) ?? false);

    const unregisterFocus = editor.registerCommand(
      FOCUS_COMMAND,
      () => {
        setIsEditorActive(true);
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );

    const unregisterBlur = editor.registerCommand(
      BLUR_COMMAND,
      () => {
        setIsEditorActive(false);
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );

    return () => {
      unregisterFocus();
      unregisterBlur();
    };
  }, [editor]);

  const uppyInstance = useMemo(() => {
    if (!isReady || !getUppyInstance) return null;

    return getUppyInstance(
      config.uppy_companion_url,
      {
        getAccessToken: currentUser._aT,
        disabledMultipartUpload: siteConfig.disabledMultipartUpload,
      },
      request,
    );
  }, [getUppyInstance, isReady, siteConfig.disabledMultipartUpload]);

  useEffect(() => {
    return () => {
      uppyInstance?.close();
    };
  }, [uppyInstance]);

  const { uppy } = useUppy(
    {
      referenceId,
      onFileAdded: (file) => {
        editor.update(() => {
          const selection = $getSelection();

          const placeholderNode = $createUploadPlaceholderNode(file.name);
          uppy?.setFileMeta(file.id, {
            ...file.meta,
            lexicalNodeKey: placeholderNode.getKey(),
          });

          if ($isRangeSelection(selection)) {
            const paragraphBefore = $createParagraphNode();
            const paragraphAfter = $createParagraphNode();
            const paragraphContainer = $createParagraphNode();
            paragraphContainer.append(placeholderNode);
            selection.insertNodes([paragraphBefore, paragraphContainer, paragraphAfter]);
          } else {
            const root = $getRoot();
            const paragraphContainer = $createParagraphNode();
            paragraphContainer.append(placeholderNode);
            const paragraphAfter = $createParagraphNode();
            root.append(paragraphContainer, paragraphAfter);
          }
        });
        return file;
      },
      onProgress: (file) => {
        const customFile = file as CustomUppyFile;
        editor.update(() => {
          const nodeKey = customFile.meta.lexicalNodeKey;
          if (!nodeKey) return;

          const node = $getNodeByKey(nodeKey);
          if (node instanceof UploadPlaceholderNode) {
            node.setProgress(file.fileProgress || 0);
          }
        });
      },
      onUploadSuccess: async (file) => {
        const customFile = file as CustomUppyFile;
        editor.update(() => {
          const nodeKey = customFile.meta.lexicalNodeKey;
          if (!nodeKey) return;

          const placeholderNode = $getNodeByKey(nodeKey);
          if (!placeholderNode) return;

          let replacementNode;
          if (file.meta.type?.startsWith('image/')) {
            replacementNode = $createImageNode(file.url || '', file.name);
          } else if (file.meta.type?.startsWith('video/')) {
            replacementNode = $createVideoNode(file.url || '');
          } else if (file.meta.type?.startsWith('audio/')) {
            replacementNode = $createAudioNode(file.url || '');
          } else {
            replacementNode = $createFileNode(
              file.url || '',
              file.meta.name || '',
              formatFileSize(file.size || 0),
            );
          }
          placeholderNode.replace(replacementNode);
        });

        return file;
      },
      onRestrictedFile: (file, error) => {
        console.error('File restricted:', file.name, error);
      },
    },
    uppyInstance,
  );

  useEffect(() => {
    if (!uppy) return;

    const editorElement = editor.getRootElement();
    if (!editorElement) {
      return;
    }

    const handlePaste = (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData || !uppy) return;

      if (clipboardData.files && clipboardData.files.length > 0) {
        event.preventDefault();
        Array.from(clipboardData.files).forEach((file) => {
          uppy.addFile({ data: file, name: `${Date.now()}-${file.name}`, type: file.type });
        });
        return;
      }

      const html = clipboardData.getData('text/html');
      if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const images = doc.querySelectorAll('img');

        if (images.length > 0) {
          event.preventDefault();
          images.forEach((image) => {
            const src = image.src;
            if (src.startsWith('http') || src.startsWith('data:')) {
              fetch(src)
                .then((res) => (res.ok ? res.blob() : null))
                .then((blob) => {
                  if (blob) {
                    let name = 'pasted-image.png';
                    if (src.startsWith('http')) {
                      try {
                        const url = new URL(src);
                        const pathParts = url.pathname.split('/');
                        const adaptedName = pathParts[pathParts.length - 1];
                        if (adaptedName) {
                          name = adaptedName;
                        }
                        if (!name.match(/\.(jpeg|jpg|gif|png|svg|webp)$/i)) {
                          name = `${name}.png`;
                        }
                      } catch (e) {
                        /* ignore */
                      }
                    }
                    uppy.addFile({
                      data: blob,
                      name: `${Date.now()}-${name}`,
                      type: blob.type,
                    });
                  }
                })
                .catch((error) => console.error('Error fetching image for upload:', error));
            }
          });
        }
      }
    };

    const handleDrop = (event: DragEvent) => {
      const dataTransfer = event.dataTransfer;
      if (!dataTransfer || !dataTransfer.files || dataTransfer.files.length === 0 || !uppy) {
        return;
      }
      event.preventDefault();
      Array.from(dataTransfer.files).forEach((file) => {
        uppy.addFile({
          data: file,
          name: `${Date.now()}-${file.name}`,
          type: file.type,
        });
      });
    };

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
    };

    editorElement.addEventListener('paste', handlePaste);
    editorElement.addEventListener('drop', handleDrop);
    editorElement.addEventListener('dragover', handleDragOver);

    return () => {
      editorElement.removeEventListener('paste', handlePaste);
      editorElement.removeEventListener('drop', handleDrop);
      editorElement.removeEventListener('dragover', handleDragOver);
    };
  }, [editor, uppy]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <Stack
      className={cn('bottom-8 right-8 absolute', className)}
      direction="row"
      justifyContent="end">
      <DeprecatedIconButton
        Icon={Attach2Fill}
        appearance="secondary"
        shape="square"
        size="small"
        variant="ghost"
        onClick={handleClick}
      />
      <FileInput
        ref={fileInputRef}
        multiple
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.har,.gif,.mp3,.wav,.zip,.ppt"
        allowDuplicateUploads={true}
        uppy={uppy}
        onUploadSuccess={() => {}}
      />
    </Stack>
  );
}
